import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { useTranslation } from "../hooks/useTranslation";

// Icono personalizado para la tienda con color #EBC122
// Crear SVG como data URL para usar con L.Icon (permite sombra)
const tiendaIconSvg = `<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg"><path fill="#EBC122" stroke="#fff" stroke-width="1.5" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.5 12.5 28.5 12.5 28.5S25 21 25 12.5C25 5.6 19.4 0 12.5 0z"/><circle cx="12.5" cy="12.5" r="4.5" fill="#fff"/></svg>`;

const tiendaIconDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(tiendaIconSvg)}`;

const tiendaIcon = new L.Icon({
  iconUrl: tiendaIconDataUrl,
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41],
  className: "tienda-marker"
});

// Icono personalizado para el cliente con color gris (#666666)
// Crear SVG como data URL para usar con L.Icon (permite sombra)
const clienteIconSvg = `<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg"><path fill="#666666" stroke="#fff" stroke-width="1.5" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.5 12.5 28.5 12.5 28.5S25 21 25 12.5C25 5.6 19.4 0 12.5 0z"/><circle cx="12.5" cy="12.5" r="4.5" fill="#fff"/></svg>`;

const clienteIconDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(clienteIconSvg)}`;

const clienteIcon = new L.Icon({
  iconUrl: clienteIconDataUrl,
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41],
  className: "cliente-marker"
});

// Coordenadas de la tienda en Cala Millor
const tienda = { lat: 39.6021, lng: 3.3845 };

// Bounding box aproximado de Mallorca
const mallorcaBounds = {
  north: 40.10,
  south: 39.15,
  west: 2.30,
  east: 3.50,
};

// Componente para manejar clics en el mapa
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    },
  });
  return null;
}

// Componente para ajustar el zoom del mapa para mostrar ambos marcadores
function MapBounds({ coords }) {
  const map = useMap();

  useEffect(() => {
    if (coords) {
      // Crear un grupo de latlngs con ambos marcadores
      const bounds = L.latLngBounds([
        [tienda.lat, tienda.lng], // Marcador de la tienda
        [coords.lat, coords.lng], // Marcador del usuario
      ]);

      // Ajustar el mapa para mostrar ambos marcadores con un padding
      map.fitBounds(bounds, {
        padding: [50, 50], // Padding en píxeles
        maxZoom: 15, // Zoom máximo para no acercarse demasiado
      });
    }
  }, [coords, map]);

  return null;
}

export default function MapaEntrega({ onAddressChange, onDistanceChange, onDeliveryCostChange, initialAddress, initialDistance }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState(initialAddress || "");
  const [coords, setCoords] = useState(null);
  const [distancia, setDistancia] = useState(initialDistance || null);
  const [direccionCompleta, setDireccionCompleta] = useState(initialAddress || "");
  const [mensaje, setMensaje] = useState(null);
  const [costoAdicional, setCostoAdicional] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isFirstSearch, setIsFirstSearch] = useState(true);
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const hasCalculatedInitialAddress = useRef(false);
  const isUserInteraction = useRef(false); // Ref para rastrear si el cambio viene del usuario
  const previousInitialAddress = useRef(initialAddress); // Ref para rastrear cambios en initialAddress
  
  // Inicializar valores cuando el componente se monta o cuando initialAddress cambia de vacío a tener valor
  useEffect(() => {
    // Si initialAddress cambió de vacío/null a tener un valor, restaurar
    const prevWasEmpty = !previousInitialAddress.current || previousInitialAddress.current.trim() === '';
    const currentHasValue = initialAddress && initialAddress.trim() !== '';
    
    if (prevWasEmpty && currentHasValue) {
      setSearch(initialAddress);
      setDireccionCompleta(initialAddress);
    }
    
    // Actualizar la referencia
    previousInitialAddress.current = initialAddress;
  }, [initialAddress]);
  
  // Calcular costoAdicional inicial si hay distancia y coste inicial
  useEffect(() => {
    if (initialDistance && initialDistance > 15) {
      const kmExtra = initialDistance - 15;
      const adicional = kmExtra * 2.5;
      setCostoAdicional(parseFloat(adicional.toFixed(2)));
    } else if (initialDistance && initialDistance <= 15) {
      setCostoAdicional(0);
    }
  }, [initialDistance]);
  
  // Restaurar valores iniciales cuando cambien las props (solo si no es interacción del usuario)
  useEffect(() => {
    // Si el cambio viene de la interacción del usuario, no hacer nada
    if (isUserInteraction.current) {
      return;
    }
    
    // Si hay una dirección inicial, restaurar los valores
    if (initialAddress && initialAddress.trim() !== '') {
      const initialAddressTrimmed = initialAddress.trim();
      const searchTrimmed = search?.trim() || '';
      const direccionCompletaTrimmed = direccionCompleta?.trim() || '';
      
      // Si los valores actuales no coinciden con la dirección inicial, restaurarlos
      // Esto es importante cuando el usuario vuelve al paso después de navegar hacia atrás
      // También restaurar si search está vacío pero initialAddress tiene valor (usuario vuelve al paso)
      if (searchTrimmed !== initialAddressTrimmed || direccionCompletaTrimmed !== initialAddressTrimmed || !searchTrimmed) {
        setSearch(initialAddress);
        setDireccionCompleta(initialAddress);
        
        // Si hay distancia inicial, restaurarla también
        if (initialDistance !== null && initialDistance !== undefined) {
          setDistancia(initialDistance);
          if (initialDistance > 15) {
            const kmExtra = initialDistance - 15;
            const adicional = kmExtra * 2.5;
            setCostoAdicional(parseFloat(adicional.toFixed(2)));
          } else {
            setCostoAdicional(0);
          }
          setMensaje(t('delivery.map.location.valid'));
        } else if (initialDistance === null || initialDistance === undefined) {
          // Si no hay distancia pero hay dirección, buscar las coordenadas para calcular la distancia
          // Esto puede pasar cuando el usuario vuelve al paso y la dirección está guardada pero no las coordenadas
          if (!coords && !isLoading) {
            // Buscar coordenadas de la dirección guardada
            const buscarCoordenadasDeDireccion = async () => {
              try {
                const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
                  initialAddress
                )}&format=json&addressdetails=1&limit=1`;

                const res = await fetch(url, {
                  headers: { "User-Agent": "BikeRentalApp/1.0" },
                });

                const data = await res.json();
                if (data.length > 0) {
                  const { lat, lon } = data[0];
                  const latNum = parseFloat(lat);
                  const lonNum = parseFloat(lon);
                  
                  // Validar que esté dentro de Mallorca
                  const dentro =
                    latNum > mallorcaBounds.south &&
                    latNum < mallorcaBounds.north &&
                    lonNum > mallorcaBounds.west &&
                    lonNum < mallorcaBounds.east;
                  
                  if (dentro) {
                    const userCoords = { lat: latNum, lng: lonNum };
                    setCoords(userCoords);
                    
                    // Calcular distancia
                    const distanciaKm = haversine(tienda, userCoords);
                    const distanciaRedondeada = parseFloat(distanciaKm.toFixed(2));
                    setDistancia(distanciaRedondeada);
                    
                    // Calcular coste adicional
                    let costoAdicionalCalculado = 0;
                    if (distanciaRedondeada > 15) {
                      const kmExtra = distanciaRedondeada - 15;
                      const adicional = kmExtra * 2.5;
                      costoAdicionalCalculado = parseFloat(adicional.toFixed(2));
                    }
                    setCostoAdicional(costoAdicionalCalculado);
                    setMensaje(t('delivery.map.location.valid'));
                    
                    // Notificar al componente padre
                    if (onDistanceChange) onDistanceChange(distanciaRedondeada);
                    const costeTotal = 40 + costoAdicionalCalculado;
                    if (onDeliveryCostChange) onDeliveryCostChange(costeTotal, distanciaRedondeada);
                  }
                }
              } catch (error) {
                console.error("Error buscando coordenadas de dirección guardada:", error);
              }
            };
            
            buscarCoordenadasDeDireccion();
          }
        }
      } else {
        // Los valores ya coinciden, solo actualizar distancia si es necesario
        if (initialDistance !== null && initialDistance !== undefined && distancia !== initialDistance) {
          setDistancia(initialDistance);
          if (initialDistance > 15) {
            const kmExtra = initialDistance - 15;
            const adicional = kmExtra * 2.5;
            setCostoAdicional(parseFloat(adicional.toFixed(2)));
          } else {
            setCostoAdicional(0);
          }
          setMensaje(t('delivery.map.location.valid'));
        }
      }
    } else if (!initialAddress || initialAddress.trim() === '') {
      // Solo limpiar si search tiene un valor diferente y no es interacción del usuario
      // y no hay coordenadas (no es una selección reciente del usuario)
      if (search && search.trim() !== '' && !coords) {
        setSearch("");
        setDireccionCompleta("");
      }
    }
    
    // Actualizar distancia cuando cambie initialDistance (independientemente de la dirección)
    if (initialDistance !== null && initialDistance !== undefined) {
      if (distancia !== initialDistance) {
        setDistancia(initialDistance);
        // Calcular coste adicional si es necesario
        if (initialDistance > 15) {
          const kmExtra = initialDistance - 15;
          const adicional = kmExtra * 2.5;
          setCostoAdicional(parseFloat(adicional.toFixed(2)));
        } else {
          setCostoAdicional(0);
        }
        // Si hay distancia y dirección, establecer mensaje de validación
        if (initialAddress && initialAddress.trim() !== '') {
          setMensaje(t('delivery.map.location.valid'));
        }
      }
    } else if (initialDistance === null || initialDistance === undefined) {
      // Solo limpiar distancia si no hay coordenadas (no es una selección reciente del usuario)
      if (!coords) {
        setDistancia(null);
      }
    }
    // No incluir 'search', 'direccionCompleta', 'coords' en las dependencias porque solo queremos actualizar cuando cambian initialAddress o initialDistance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAddress, initialDistance]);

  // Haversine para la distancia
  const haversine = (c1, c2) => {
    const R = 6371;
    const dLat = ((c2.lat - c1.lat) * Math.PI) / 180;
    const dLng = ((c2.lng - c1.lng) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((c1.lat * Math.PI) / 180) *
        Math.cos((c2.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;

    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Reverse geocoding: obtener dirección desde coordenadas
  const obtenerDireccionDesdeCoords = async (lat, lng) => {
    setIsLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`;
      
      const res = await fetch(url, {
        headers: { "User-Agent": "BikeRentalApp/1.0" },
      });

      const data = await res.json();
      
      if (data && data.display_name) {
        return data.display_name;
      }
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } catch (error) {
      console.error("Error en reverse geocoding:", error);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } finally {
      setIsLoading(false);
    }
  };

  // Procesar coordenadas y calcular distancia
  const procesarCoordenadas = async (lat, lng, direccionPredefinida = null) => {
    // Mantener el flag activo durante todo el proceso
    isUserInteraction.current = true;
    
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lng);

    // Validar Mallorca con bounding box
    const dentro =
      latNum > mallorcaBounds.south &&
      latNum < mallorcaBounds.north &&
      lonNum > mallorcaBounds.west &&
      lonNum < mallorcaBounds.east;

    if (!dentro) {
      setMensaje(t('delivery.map.location.invalid'));
      setCoords(null);
      setDistancia(null);
      setDireccionCompleta("");
      setCostoAdicional(null);
      if (onAddressChange) onAddressChange("");
      if (onDistanceChange) onDistanceChange(null);
      if (onDeliveryCostChange) onDeliveryCostChange(null);
      // Resetear el flag después de un delay
      setTimeout(() => {
        isUserInteraction.current = false;
      }, 500);
      return;
    }

    // Si está dentro, guardamos coordenadas
    const userCoords = { lat: latNum, lng: lonNum };
    setCoords(userCoords);

    // Usar la dirección predefinida si está disponible, sino obtener desde coordenadas
    let direccion;
    if (direccionPredefinida) {
      direccion = direccionPredefinida;
    } else {
      direccion = await obtenerDireccionDesdeCoords(latNum, lonNum);
    }
    setDireccionCompleta(direccion);
    setSearch(direccion);

    // Calculamos distancia
    const distanciaKm = haversine(tienda, userCoords);
    const distanciaRedondeada = parseFloat(distanciaKm.toFixed(2));
    setDistancia(distanciaRedondeada);

    // Calcular coste adicional si es mayor a 15 km
    let costoAdicionalCalculado = 0;
    if (distanciaRedondeada > 15) {
      const kmExtra = distanciaRedondeada - 15;
      const adicional = kmExtra * 2.5;
      costoAdicionalCalculado = parseFloat(adicional.toFixed(2));
    }
    setCostoAdicional(costoAdicionalCalculado);

    setMensaje("✔️ Ubicación válida dentro de Mallorca");

    // Calcular coste total de entrega (40€ base + suplemento)
    const costeTotal = 40 + costoAdicionalCalculado;

    // Notificar al componente padre - asegurarse de que siempre se llame con la dirección completa
    if (onAddressChange && direccion) {
      onAddressChange(direccion);
    }
    if (onDistanceChange) onDistanceChange(distanciaRedondeada);
    // Pasar también la distancia cuando se actualiza el coste para guardarlo en el contexto
    if (onDeliveryCostChange) onDeliveryCostChange(costeTotal, distanciaRedondeada);
    
    // Mantener el flag activo más tiempo después de completar para evitar que el efecto interfiera
    // El tiempo más largo permite que las actualizaciones del contexto se completen antes de resetear
    setTimeout(() => {
      isUserInteraction.current = false;
    }, 2000);
  };

  // Buscar dirección con Nominatim
  const buscarDireccion = async () => {
    if (!search.trim()) return;
    
    isUserInteraction.current = true; // Marcar como interacción del usuario
    setSuggestionSelected(true); // Marcar como seleccionado para evitar sugerencias
    setShowSuggestions(false); // Ocultar sugerencias inmediatamente
    setSuggestions([]); // Limpiar sugerencias
    setMensaje(null);
    setDistancia(null);
    setIsLoading(true);

    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        search
      )}&format=json&addressdetails=1&limit=1`;

      const res = await fetch(url, {
        headers: { "User-Agent": "BikeRentalApp/1.0" },
      });

      const data = await res.json();
      if (data.length === 0) {
        setMensaje(t('delivery.map.address.not.found'));
        setCoords(null);
        setDistancia(null);
        setDireccionCompleta("");
        setCostoAdicional(null);
        if (onAddressChange) onAddressChange("");
        if (onDistanceChange) onDistanceChange(null);
        if (onDeliveryCostChange) onDeliveryCostChange(null);
        isUserInteraction.current = false; // Resetear en caso de no encontrar dirección
        return;
      }

      const { lat, lon } = data[0];
      await procesarCoordenadas(lat, lon);
      // Mantener el flag activo un poco más para evitar que el efecto interfiera
      setTimeout(() => {
        isUserInteraction.current = false;
      }, 500);
    } catch {
      setMensaje(t('delivery.map.address.error'));
      isUserInteraction.current = false; // Resetear en caso de error
    } finally {
      setIsLoading(false);
    }
  };

  // Manejar clic en el mapa
  const handleMapClick = async (latlng) => {
    isUserInteraction.current = true; // Marcar como interacción del usuario
    setSuggestionSelected(true); // Marcar como seleccionado para evitar sugerencias
    setShowSuggestions(false); // Ocultar sugerencias inmediatamente
    setSuggestions([]); // Limpiar sugerencias
    await procesarCoordenadas(latlng.lat, latlng.lng);
    // El flag se resetea dentro de procesarCoordenadas después de completar
  };

  // Autocompletado: buscar sugerencias mientras se escribe
  const buscarSugerencias = useCallback(async (query) => {
    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      // Usar viewbox para limitar búsqueda a Mallorca
      // viewbox=min_lon,min_lat,max_lon,max_lat
      const viewbox = `${mallorcaBounds.west},${mallorcaBounds.south},${mallorcaBounds.east},${mallorcaBounds.north}`;
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        query
      )}&format=json&addressdetails=1&limit=5&viewbox=${viewbox}&bounded=1`;

      const res = await fetch(url, {
        headers: { "User-Agent": "BikeRentalApp/1.0" },
      });

      const data = await res.json();
      
      // Filtrar resultados para asegurar que están dentro de Mallorca
      const filtered = data.filter((item) => {
        const lat = parseFloat(item.lat);
        const lon = parseFloat(item.lon);
        return (
          lat > mallorcaBounds.south &&
          lat < mallorcaBounds.north &&
          lon > mallorcaBounds.west &&
          lon < mallorcaBounds.east
        );
      });

      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error("Error buscando sugerencias:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  // Debounce para las sugerencias (más rápido la primera vez)
  useEffect(() => {
    // No buscar sugerencias si se ha seleccionado una sugerencia
    // Permitir búsqueda de sugerencias mientras el usuario escribe, incluso si isUserInteraction está activo
    if (suggestionSelected) {
      return;
    }

    // Usar 100ms para la primera búsqueda, 300ms para las siguientes
    const debounceTime = isFirstSearch ? 100 : 300;
    
    const timer = setTimeout(() => {
      // Buscar sugerencias si el texto tiene al menos 3 caracteres
      if (search.trim().length >= 3) {
        buscarSugerencias(search);
        // Marcar que ya no es la primera búsqueda después de la primera búsqueda exitosa
        if (isFirstSearch) {
          setIsFirstSearch(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, debounceTime);

    return () => clearTimeout(timer);
  }, [search, buscarSugerencias, isFirstSearch, suggestionSelected]);

  // Seleccionar una sugerencia
  const seleccionarSugerencia = async (sugerencia) => {
    isUserInteraction.current = true; // Marcar como interacción del usuario
    const direccionSugerencia = sugerencia.display_name;
    setSearch(direccionSugerencia);
    setShowSuggestions(false);
    setSuggestions([]);
    setSuggestionSelected(true); // Marcar que se ha seleccionado una sugerencia
    setIsFirstSearch(true); // Resetear para la próxima búsqueda
    // Pasar la dirección de la sugerencia para usarla directamente en lugar de hacer reverse geocoding
    await procesarCoordenadas(sugerencia.lat, sugerencia.lon, direccionSugerencia);
    // El flag se resetea dentro de procesarCoordenadas después de completar
  };

  // Manejar teclas en el input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        seleccionarSugerencia(suggestions[selectedIndex]);
      } else {
        buscarDireccion();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Calcular coordenadas automáticamente cuando hay una dirección inicial pero no hay coordenadas
  // Este efecto es un fallback para cuando el usuario vuelve al paso y hay dirección pero no coordenadas
  // El efecto de restauración principal maneja la mayoría de los casos, pero este asegura que
  // las coordenadas se calculen si por alguna razón no se calcularon antes
  useEffect(() => {
    // Solo ejecutar si no hay interacción del usuario activa
    if (isUserInteraction.current) {
      return;
    }
    
    // Si hay una dirección inicial, distancia inicial, pero no hay coordenadas, buscar las coordenadas
    // Esto asegura que el mapa muestre el marcador cuando el usuario vuelve al paso
    if (initialAddress && initialAddress.trim() && 
        initialDistance !== null && initialDistance !== undefined &&
        !coords && !isLoading) {
      
      const initialAddressTrimmed = initialAddress.trim();
      const searchTrimmed = search?.trim() || '';
      
      // Solo buscar si la dirección actual coincide con la inicial (ya fue restaurada)
      // y no se ha calculado ya para esta dirección
      if (searchTrimmed === initialAddressTrimmed) {
        const direccionKey = initialAddressTrimmed;
        if (!hasCalculatedInitialAddress.current || hasCalculatedInitialAddress.current !== direccionKey) {
          hasCalculatedInitialAddress.current = direccionKey;
          
          // Buscar las coordenadas para mostrar el marcador en el mapa
          const buscarCoordenadasParaMarcador = async () => {
            try {
              const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
                initialAddress
              )}&format=json&addressdetails=1&limit=1`;

              const res = await fetch(url, {
                headers: { "User-Agent": "BikeRentalApp/1.0" },
              });

              const data = await res.json();
              if (data.length > 0) {
                const { lat, lon } = data[0];
                const latNum = parseFloat(lat);
                const lonNum = parseFloat(lon);
                
                // Validar que esté dentro de Mallorca
                const dentro =
                  latNum > mallorcaBounds.south &&
                  latNum < mallorcaBounds.north &&
                  lonNum > mallorcaBounds.west &&
                  lonNum < mallorcaBounds.east;
                
                if (dentro) {
                  // Solo establecer las coordenadas para mostrar el marcador
                  // No llamar a procesarCoordenadas porque ya tenemos la distancia
                  setCoords({ lat: latNum, lng: lonNum });
                }
              }
            } catch (error) {
              console.error("Error buscando coordenadas para marcador:", error);
              hasCalculatedInitialAddress.current = false; // Permitir reintentar en caso de error
            }
          };

          buscarCoordenadasParaMarcador();
        }
      }
    }
    
    // Resetear el flag si la dirección inicial cambia o se limpia
    if (!initialAddress || initialAddress.trim() === '') {
      hasCalculatedInitialAddress.current = false;
    }
  }, [initialAddress, initialDistance, coords, isLoading, search]);

  return (
    <div className="bg-background border border-primary rounded-xl p-6 mb-6">
      <div className="flex items-center mb-4">
        <div className="p-2 bg-primary/10 text-primary rounded-full mr-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-2c3e50">{t('delivery.map.title')}</h3>
      </div>

      <div className="mb-4">
        <div className="flex gap-2 mb-3 relative" ref={searchRef}>
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder={t('delivery.map.search.placeholder')}
              value={search}
              onChange={(e) => {
                isUserInteraction.current = true; // Marcar como interacción del usuario
                const newValue = e.target.value;
                setSearch(newValue);
                // Si el usuario modifica el texto manualmente, permitir mostrar sugerencias de nuevo
                setSuggestionSelected(false);
                // Si el campo está vacío o casi vacío, resetear para la próxima búsqueda
                if (newValue.trim().length < 3) {
                  setIsFirstSearch(true);
                  setSuggestions([]);
                  setShowSuggestions(false);
                }
                // No establecer showSuggestions aquí, dejar que el efecto de debounce lo maneje
                // cuando las sugerencias estén listas
              }}
              onKeyDown={handleKeyPress}
              onFocus={() => {
                if (suggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              className="w-full bg-white border border-gray-300 text-2c3e50 rounded-lg px-4 py-2 text-sm outline-none transition-all duration-200 focus:border-EBC122 focus:ring-1 focus:ring-EBC122"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
              >
                {suggestions.map((sugerencia, index) => (
                  <div
                    key={index}
                    onClick={() => seleccionarSugerencia(sugerencia)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`px-4 py-3 cursor-pointer transition-colors ${
                      index === selectedIndex
                        ? "bg-primary/20 text-2c3e50"
                        : "hover:bg-gray-50 text-2c3e50"
                    } ${index === 0 ? "rounded-t-lg" : ""} ${
                      index === suggestions.length - 1 ? "rounded-b-lg" : "border-b border-gray-200"
                    }`}
                  >
                    <div className="font-medium text-sm">{sugerencia.display_name}</div>
                    {sugerencia.address && (
                      <div className="text-xs text-gray-500 mt-1">
                        {sugerencia.address.city || sugerencia.address.town || sugerencia.address.village || ""}
                        {sugerencia.address.postcode && ` - ${sugerencia.address.postcode}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={buscarDireccion}
            disabled={isLoading || !search.trim()}
            className="px-6 py-2 bg-primary text-2c3e50 font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-opacity-90 transition-all"
          >
            {isLoading ? t('delivery.map.searching') : t('delivery.map.search')}
          </button>
        </div>

        {mensaje && (
          <div className={`mb-3 p-3 rounded-lg text-sm ${
            mensaje.includes("❌") 
              ? "bg-red-50 border border-red-200 text-red-700" 
              : "bg-green-50 border border-green-200 text-green-700"
          }`}>
            {mensaje}
          </div>
        )}

        {distancia !== null && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
            <div className="space-y-2">
              <p className="text-sm text-2c3e50">
                <strong>{t('delivery.map.distance.to.store')}</strong>{" "}
                <span className="text-EBC122 font-bold">{distancia} km</span>
              </p>
              {direccionCompleta && (
                <p className="text-xs text-6c757d">
                  <strong>{t('delivery.map.address.label')}</strong> {direccionCompleta}
                </p>
              )}
              {costoAdicional !== null && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-sm text-2c3e50">
                    <strong>{t('delivery.map.base.cost')}</strong> {t('delivery.map.base.cost.includes', { km: '15' })}
                  </p>
                  {costoAdicional > 0 && (
                    <p className="text-sm text-2c3e50 mt-1">
                      <strong>{t('delivery.map.supplement', { km: (distancia - 15).toFixed(2) })}</strong>{" "}
                      <span className="text-EBC122 font-bold">+{costoAdicional.toFixed(2)} €</span>
                    </p>
                  )}
                  <p className="text-sm font-semibold text-2c3e50 mt-2">
                    <strong>{t('delivery.map.total.delivery')}</strong>{" "}
                    <span className="text-EBC122 font-bold text-lg">
                      {(40 + costoAdicional).toFixed(2)} €
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {coords ? (
          <div className="mb-3 p-3 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-800">
            <p className="font-medium mb-1">📍 {t('delivery.map.address.selected')}</p>
            <p className="text-xs">
              {t('delivery.map.address.correct')}
            </p>
          </div>
        ) : (
          <p className="text-xs text-6c757d mb-3">
            💡 {t('delivery.map.help')}
          </p>
        )}
      </div>

      <div className="relative" style={{ height: "500px", borderRadius: "12px", overflow: "hidden" }}>
        <MapContainer
          center={[39.65, 3.00]}
          zoom={10}
          style={{ height: "100%", width: "100%", zIndex: 0 }}
          scrollWheelZoom={true}
        >
          <TileLayer
            url={
              import.meta.env.VITE_CARTO_API_KEY
                ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${import.meta.env.VITE_CARTO_API_KEY}`
                : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            }
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            subdomains={import.meta.env.VITE_CARTO_API_KEY ? "abcd" : "abc"}
          />

          <MapClickHandler onMapClick={handleMapClick} />
          
          {/* Ajustar zoom para mostrar ambos marcadores */}
          <MapBounds coords={coords} />

          {/* Marcador de la tienda */}
          <Marker position={[tienda.lat, tienda.lng]} icon={tiendaIcon}>
            <Popup>
              <div className="text-center">
                <strong>📍 {t('delivery.map.our.store')}</strong>
                <br />
                {t('delivery.map.store.location')}
              </div>
            </Popup>
          </Marker>

          {/* Marcador de la dirección del cliente */}
          {coords && (
            <Marker position={[coords.lat, coords.lng]} icon={clienteIcon}>
              <Popup>
                <div className="text-center">
                  <strong>📍 {t('delivery.map.your.location')}</strong>
                  <br />
                  {direccionCompleta || t('delivery.map.location.selected')}
                  <br />
                  <span className="text-sm text-gray-600">
                    {t('delivery.map.distance')}: {distancia} km
                  </span>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
    </div>
  );
}
